import { Injectable } from '@angular/core';
import { ApplicationStateService } from './application-state.service';
import { Position } from '../models/position';
import { environment } from '../../environments/environment';
import { ZoomEvent } from '../utils/events/zoom-event';
import { CommunicationService } from './communication.service';
import { PositioningService } from './positioning.service';
import { PagesComponent } from '../components/bookviewer/pages/pages.component';

@Injectable({
    providedIn: 'root'
})
export class ZoomService {

    lastZoomPoint: Point;
    lastRelativeZoomPoint: Point;
    oldZoomLevel: number;

    lastTranslateX = 0;
    lastTranslateY = 0;

    constructor(private applicationStateService: ApplicationStateService,
                private communicationService: CommunicationService,
                private positioningService: PositioningService) {
    }

    /**
     * Zoom amount of steps in.
     *
     * @param {number} steps
     * @param {Point} mousePoint
     */
    zoomStepsIn(steps: number, mousePoint?: Point): void {
        this.zoomToLevel(
            this.applicationStateService.zoomLevel + steps * this.getSingleZoomStep(),
            mousePoint
        );
    }

    /**
     * Zoom amount of steps out.
     *
     * @param {number} steps
     * @param {Point} mousePoint
     */
    zoomStepsOut(steps: number, mousePoint?: Point): void {
        this.zoomToLevel(
            this.applicationStateService.zoomLevel - steps * this.getSingleZoomStep(),
            mousePoint
        );
    }

    /**
     * Zoom one step in.
     *
     * @param {Point} mousePoint
     */
    zoomOneStepIn(mousePoint?: Point): void {
        this.zoomStepsIn(1, mousePoint);
    }

    /**
     * Zoom one step out.
     *
     * @param {Point} mousePoint
     */
    zoomOneStepOut(mousePoint?: Point): void {
        this.zoomStepsOut(1, mousePoint);
    }

    /**
     * Zoom to max zoom level.
     *
     * @param {Point} mousePoint
     */
    zoomMaxIn(mousePoint?: Point): void {
        this.zoomToLevel(this.getMaxZoomLevel(), mousePoint);
    }

    /**
     * Zoom to min zoom level.
     *
     * @param {Point} mousePoint
     */
    zoomMaxOut(mousePoint?: Point): void {
        this.zoomToLevel(this.getMinZoomLevel(), mousePoint);
    }

    /**
     * Determine if the current zoom level is the minimum zoom level.
     *
     * @returns {boolean}
     */
    isMinZoomed(): boolean {
        return (this.applicationStateService.zoomLevel === this.getMinZoomLevel());
    }

    /**
     * Determine if the current zoom level is the maximum zoom level.
     *
     * @returns {boolean}
     */
    isMaxZoomed(): boolean {
        return (this.applicationStateService.zoomLevel === this.getMaxZoomLevel());
    }

    /**
     * Get minimum zoom level.
     *
     * @returns {number}
     */
    getMinZoomLevel(): number {
        return environment.minZoomLevel;
    }

    /**
     * Get maximum zoom level.
     *
     * @returns {number}
     */
    getMaxZoomLevel(): number {
        return environment.maxZoomLevel;
    }

    /**
     * Get current zoom level.
     *
     * @returns {number}
     */
    getCurrentZoomLevel(): number {
        return this.applicationStateService.zoomLevel;
    }

    /**
     * Zoom to a specific level.
     *
     * @param {number} zoomLevel
     * @param {Point} mousePoint
     */
    zoomToLevel(zoomLevel: number, mousePoint?: Point): void {
        this.applicationStateService.zoomLevel = zoomLevel;
        const zoomEvent = ZoomEvent.createZoomEvent(this.applicationStateService.zoomLevel, mousePoint);
        this.communicationService.announceZoomLevelChanged(zoomEvent);
    }

    /**
     * Scale the page to the given zoom level.
     *
     * @param {PagesComponent} pagesComponent
     * @param {number} zoomLevel
     * @param {Point} zoomPoint
     *
     * @returns {ZoomProperties}
     */
    scalePages(pagesComponent: PagesComponent, zoomLevel: number, zoomPoint?: Point): ZoomProperties {

        const $viewport: JQuery = pagesComponent.getContainer();
        const $container: JQuery = pagesComponent.getPagesContainer();

        if (!zoomPoint) {
            // Set zoom point to half the viewport if not set
            zoomPoint = new Point({
                x: $viewport.width() / 2,
                y: $viewport.height() / 2
            });
        }

        const originalContainerSize: Position = this.positioningService.getPagesContainerSize();
        const containerPosition: Position = new Position();
        containerPosition.x = $container.offset().left;
        containerPosition.y = $container.offset().top;
        containerPosition.w = $container.width();
        containerPosition.h = $container.height();

        let currentTranslateX = this.lastTranslateX;
        let currentTranslateY = this.lastTranslateY;

        if ($container.css('transform')
            .split(',')[4] && $container.css('transform')
            .split(',')[5]) {
            currentTranslateX = pagesComponent.containerTranslateX;
            currentTranslateY = pagesComponent.containerTranslateY;
        }

        if (!this.lastZoomPoint) {
            this.lastZoomPoint = zoomPoint;
        }

        this.lastRelativeZoomPoint = this.getRelativeZoomPoint(this.lastZoomPoint, containerPosition);
        this.lastZoomPoint = zoomPoint;

        const relativeZoomPoint: Point = this.getRelativeZoomPoint(
            zoomPoint,
            containerPosition
        );

        const scaledWidth: number = originalContainerSize.w * zoomLevel;
        const scaledHeight: number = originalContainerSize.h * zoomLevel;

        let translateX: number = (-scaledWidth + containerPosition.w) * relativeZoomPoint.x;
        let translateY: number = (-scaledHeight + containerPosition.h) * relativeZoomPoint.y;

        // Add the amount the container will be resized to the translation
        // (Half because the container is always centered)
        translateX += (scaledWidth - containerPosition.w) / 2;
        translateY += (scaledHeight - containerPosition.h) / 2;

        // Added old translate value
        translateX += currentTranslateX;
        translateY += currentTranslateY;

        // Limit translate when zoomed out
        translateX = Math.min(translateX, (scaledWidth - originalContainerSize.w));
        translateY = Math.min(translateY, (scaledHeight - originalContainerSize.h));
        translateX = Math.max(translateX, -(scaledWidth - originalContainerSize.w));
        translateY = Math.max(translateY, -(scaledHeight - originalContainerSize.h));

        const zoomProperties: ZoomProperties = new ZoomProperties();
        zoomProperties.scaledWidth = scaledWidth;
        zoomProperties.scaledHeight = scaledHeight;
        zoomProperties.translateX = translateX;
        zoomProperties.translateY = translateY;
        this.lastTranslateX = translateX;
        this.lastTranslateY = translateY;

        this.oldZoomLevel = zoomLevel;

        /*let debugEnd = new Date().getMilliseconds() - debugStart;
        console.log(debugEnd);*/

        return zoomProperties;
    }

    /**
     * Returns the last known zoom point
     *
     * @returns {Point}
     */
    getLastZoomPoint(): Point {
        return this.lastZoomPoint;
    }

    /**
     * Get zoom level of one zoom step.
     *
     * @returns {number}
     */
    private getSingleZoomStep(): number {
        return (this.getMaxZoomLevel() - this.getMinZoomLevel()) / environment.amountZoomLevels;
    }

    /*private getAbsoluteZoomPoint(relativeZoomPoint: Point, containerPosition: Position): Point {

        const absoluteX: number = relativeZoomPoint.x * containerPosition.w + containerPosition.x;
        const absoluteY: number = relativeZoomPoint.y * containerPosition.h + containerPosition.y;

        return new Point({
            x: absoluteX,
            y: absoluteY
        });
    }*/

    /**
     * Get the relative offsets of the given zoom point on the container.
     *
     * @param {Point} zoomPoint
     * @param {Position} containerPosition
     *
     * @returns {Point}
     */
    private getRelativeZoomPoint(zoomPoint: Point, containerPosition: Position): Point {

        const relativeX: number = (zoomPoint.x - containerPosition.x) / containerPosition.w;
        const relativeY: number = (zoomPoint.y - containerPosition.y) / containerPosition.h;

        return new Point({
            x: relativeX,
            y: relativeY
        });
    }
}

export class ZoomProperties {
    scaledWidth: number;
    scaledHeight: number;

    translateX: number;
    translateY: number;
}

export class Point {
    x: number;
    y: number;

    constructor(obj: any) {
        this.x = obj.x;
        this.y = obj.y;
    }
}
